/**
 * @copyright Copyright 2023-2025 Epic Systems Corporation
 * @file Request download button in Showroom
 * @module Epic.AppOrchard.Core.ShowroomRequestDownloadButton
 */

import { As, Box, Button, Flex, GridItem, Heading, Text } from "@chakra-ui/react";
import { ISuccessHandler, useAsync } from "@epic/react-async-hook";
import { config } from "ao/appConfig";
import { AgreementModal, AOLink, StatusAlert, StatusModal } from "ao/components/Core";
import { requestDownload } from "ao/components/Showroom/data/data";
import { ConsumerTypes } from "ao/data";
import { useSiteInfoState } from "ao/state/siteInfo";
import { IUserSecurity } from "ao/types";
import { IShowroomAppDownloadModel } from "ao/types/showroom";
import { getBaseUrl, isLocalhost } from "ao/utils/helpers";
import { isInFlight } from "ao/utils/useAsyncHelpers";
import { ChangeEvent, FC, memo, useCallback, useMemo, useState } from "react";

interface IProps {
	appDetails: IShowroomAppDownloadModel;
	headerColor: string;
	buttonBackgroundColor: string;
	buttonBorderColor: string;
	userSecurity: IUserSecurity;
	reload: () => void;
	buttonsAs?: As;
	isShowroom?: boolean;
}

export const ShowroomRequestDownloadButton: FC<IProps> = memo((props: IProps) => {
	//#region state and functions

	const {
		appDetails,
		headerColor,
		buttonBackgroundColor,
		buttonBorderColor,
		userSecurity,
		reload,
		buttonsAs,
		isShowroom,
	} = props;

	const [showAgreementModal, setShowAgreementModal] = useState<boolean>(false);
	const [showAppTermsModal, setShowAppTermsModal] = useState<boolean>(false);
	const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
	const [acceptChecked, setAcceptedChecked] = useState<boolean>(false);
	const [saveError, setSaveError] = useState<string>("");

	const { siteInfo } = useSiteInfoState((selectors) => selectors.getState(), []);
	const isShowroomPublic = isShowroom && !isLocalhost() && !siteInfo.isAdminSite;
	const baseUrl = useMemo(() => (isShowroomPublic ? siteInfo.vendorServicesUrl : getBaseUrl()), [
		isShowroomPublic,
		siteInfo.vendorServicesUrl,
	]);

	const showroomBaseUrl = useMemo(() => getBaseUrl(), []);

	const appModel = appDetails.appModel;
	const vendorName = appModel.orgName ?? "the vendor";

	const hasAppTerms = appModel.agreementId !== null && appModel.agreementId > 0;
	const hasParticipantTerms = appDetails.participantTermsId !== null && appDetails.participantTermsId > 0;
	const isAppAgreementFacilitated = hasAppTerms && appModel.isAppAgreementFacilitated && isShowroom;

	const appAgreementUrl = isShowroomPublic
		? `${showroomBaseUrl}VendorServices/api/Showroom/AppAgreement?termId=${appModel.agreementId}&download=false`
		: `${baseUrl}Developer/AppAgreement?termId=${appModel.agreementId}`;

	const appAgreementDownloadUrl = isShowroomPublic
		? `${showroomBaseUrl}VendorServices/api/Showroom/AppAgreement?termId=${appModel.agreementId}&download=true`
		: `${baseUrl}Developer/AppAgreement?termId=${appModel.agreementId}&download=true`;

	const participantAgreementUrl = `${baseUrl}Gallery/VendorServicesCustomerAgreement?termsId=${appDetails.participantTermsId}`;
	const participantAgreementDownloadUrl = `${baseUrl}Gallery/VendorServicesCustomerAgreement?termsId=${appDetails.participantTermsId}&download=true`;

	const handleModalClose = useCallback(() => {
		setAcceptedChecked(false);
		setShowAgreementModal(false);
		setShowAppTermsModal(false);
	}, []);

	const handleAcceptChecked = useCallback(
		(ev: ChangeEvent<HTMLInputElement>) => setAcceptedChecked(ev.target.checked),
		[],
	);

	const handleRequestDownloadSuccess = useCallback<ISuccessHandler<typeof requestDownload>>((response) => {
		if (response.Success) {
			setShowSuccessModal(true);
		} else {
			setSaveError(response.Message);
		}
	}, []);

	const [requestingDownloadState, requestAppDownload] = useAsync(requestDownload, {
		executeImmediately: false,
		displayName: "requestDownload",
		onSuccess: handleRequestDownloadSuccess,
		onFailure: () =>
			setSaveError(
				"Please try again later or contact your Epic representative if this continues to happen.",
			),
	});

	const handleRequestDownload = useCallback(() => {
		const appTerms = isAppAgreementFacilitated ? null : appModel.agreementId;
		requestAppDownload(
			appDetails.appId!,
			appDetails.participantTermsId,
			appTerms,
			appDetails.accessTokenValidity,
			appDetails.refreshTokenValidity,
			appDetails.disableIndefiniteAccess,
		);
	}, [
		isAppAgreementFacilitated,
		appModel.agreementId,
		requestAppDownload,
		appDetails.appId,
		appDetails.participantTermsId,
		appDetails.accessTokenValidity,
		appDetails.refreshTokenValidity,
		appDetails.disableIndefiniteAccess,
	]);

	const handleParticipantTermsAccepted = useCallback(() => {
		setShowAgreementModal(false);
		setAcceptedChecked(false);
		if (hasAppTerms && !isAppAgreementFacilitated) {
			setShowAppTermsModal(true);
		} else {
			handleRequestDownload();
		}
	}, [handleRequestDownload, hasAppTerms, isAppAgreementFacilitated]);

	const handleAppTermsAccepted = useCallback(() => {
		setShowAppTermsModal(false);
		setAcceptedChecked(false);
		handleRequestDownload();
	}, [handleRequestDownload]);

	const handleRequestClicked = useCallback(() => {
		if (appModel.isEpicOnFhirApp) {
			handleRequestDownload();
		} else if (hasParticipantTerms) {
			setShowAgreementModal(true);
		} else {
			handleParticipantTermsAccepted();
		}
	}, [
		appModel.isEpicOnFhirApp,
		handleParticipantTermsAccepted,
		handleRequestDownload,
		hasParticipantTerms,
	]);

	const isRequesting = isInFlight(requestingDownloadState);

	const buttonTitle = `Request product creator approval to download the product's client IDs or other product information available through the ${config.SiteName} website.`;

	const isBackend = appModel.consumerType === ConsumerTypes.Backend;
	const isOwnApp = appModel.organizationId === userSecurity.userOrganizationId;

	const canDownloadAndActivate =
		!isOwnApp &&
		appModel.isEpicOnFhirApp &&
		!appModel.isConfidentialClient &&
		!isBackend &&
		!(appModel.isRelatedOrgApp && !appDetails.isDownloaded);

	const isDownloadAvailable =
		userSecurity.canPurchase &&
		appModel.showDownloadLink &&
		!appModel.isAutoDownloadedClient &&
		!appDetails.isDownloaded &&
		!isOwnApp &&
		!appModel.requiresFutureEpicVersion &&
		!appModel.isRelatedOrgApp;
	const showSection =
		appDetails.previousAPILicenseExists ||
		appDetails.isUSCDIDownloadSet ||
		appDetails.appCanBeDownloadedWithoutApiLicense;

	const downloadHeaderText = "Ready to Set Up This Product in Your System?";
	let infoText = "";

	const buttonText = canDownloadAndActivate ? "Download Client IDs" : "Request Client IDs";

	if (isDownloadAvailable) {
		infoText = canDownloadAndActivate
			? "This will download and activate both the non-production client ID and the production client ID."
			: `Sending a client ID request will notify ${vendorName} of your interest and indicate that you're ready to begin the app implementation. The same request will be used for both the non-production and production client IDs.`;
	}

	if (isOwnApp) {
		infoText = appModel.ownDownloadExists
			? "This product was created by your organization so you do not need to request download through this page. This product is active in your environments."
			: "This product was created by your organization so you do not need to request download through this page. A user from your organization with the Can Activate Clients permission can activate this product for your environments.";
	}

	if (appModel.isRelatedOrgApp) {
		infoText = appDetails.isDownloaded
			? `This product was created by an organization related to your organization, ${vendorName}. This product is active in your environments.`
			: `This product was created by an organization related to your organization, ${vendorName}, so you do not need to request download through this page. Instead, the product should be activated by a user from your organization with the Can Activate Clients permission via the homegrown apps activation workflow.`;
	}

	if (appModel.requiresFutureEpicVersion) {
		infoText = "This product uses features that are not supported on your current Epic version.";
	}

	//#endregion

	const DownloadButton = buttonsAs || Button;

	const justify = "center";

	return (
		<>
			{showSection && (isDownloadAvailable || infoText) && (
				<>
					<GridItem gridArea="header2">
						{isDownloadAvailable && (
							<Heading
								as="h3"
								color={headerColor}
								textAlign={justify}
								fontFamily={isShowroom ? "Jost" : "unset"}
							>
								{downloadHeaderText}
							</Heading>
						)}
					</GridItem>
					<GridItem gridArea="middle2">
						{infoText && (
							<Text textAlign={justify} fontWeight="300" pt="8px" pb="20px">
								{infoText}
							</Text>
						)}
						{isDownloadAvailable && appDetails.hasPotentiallyUnsafeProviderFacingDCR && (
							<StatusAlert
								mb="0.5em"
								message={
									<Box>
										This provider-facing app uses dynamic client registration (DCR) and
										your system is on an Epic version before the Epic Nov 2023 version,
										which resolved some issues with provider-facing DCR. This app may run
										into issues depending on the specifics of the OAuth 2.0 workflow.
										Contact the vendor and see the{" "}
										<AOLink
											url={`${baseUrl}Article/Index?docId=OAuth2&section=Standalone-Oauth2-OfflineAccess-0`}
											isExternal
										>
											documentation on dynamic client registration
										</AOLink>{" "}
										for more details.
									</Box>
								}
								status="warning"
							/>
						)}
					</GridItem>

					<GridItem gridArea="button2">
						{isDownloadAvailable && (
							<Flex justify={justify}>
								<DownloadButton
									aria-label={buttonText}
									title={buttonTitle}
									onClick={handleRequestClicked}
									disabled={isRequesting}
									isLoading={isRequesting}
									backgroundColor={buttonBackgroundColor}
									border={`solid 1px ${buttonBorderColor}`}
								>
									{buttonText}
								</DownloadButton>
							</Flex>
						)}
					</GridItem>

					{hasParticipantTerms && (
						<AgreementModal
							isOpen={showAgreementModal}
							header="Terms of Service"
							acceptChecked={acceptChecked}
							termsUrl={participantAgreementUrl}
							downloadTermsUrl={participantAgreementDownloadUrl}
							termsAriaLabel="Participant Agreement Terms"
							acceptCaption={hasAppTerms ? "Agree" : "I Accept"}
							onClose={handleModalClose}
							onAgree={handleParticipantTermsAccepted}
							onAcceptCheckedChanged={handleAcceptChecked}
							downloadFileName="Terms of Service.pdf"
						/>
					)}
					{hasAppTerms && (
						<AgreementModal
							isOpen={showAppTermsModal}
							header="Application Agreement"
							acceptChecked={acceptChecked}
							termsUrl={appAgreementUrl}
							downloadTermsUrl={appAgreementDownloadUrl}
							termsAriaLabel="Application Agreement Terms"
							acceptCaption="I accept"
							onClose={handleModalClose}
							onAgree={handleAppTermsAccepted}
							onAcceptCheckedChanged={handleAcceptChecked}
							downloadFileName={`${appModel.name} Application Agreement.pdf`}
						/>
					)}
					<StatusModal
						title="Error"
						message={saveError}
						isOpen={!!saveError}
						onClose={() => setSaveError("")}
						status="error"
					/>
					<StatusModal
						title="Success"
						message={
							<>
								<Box>
									{canDownloadAndActivate
										? "Successfully associated this product with your organization, the client IDs will be downloaded into your system within a day."
										: `Your client ID download request was successfully submitted to ${vendorName}. This product's client IDs will be downloaded into your system after they approve your request.`}
								</Box>
								<Box my="5px">
									See{" "}
									<AOLink url={`${baseUrl}Picked`} isExternal>
										Picked Apps
									</AOLink>{" "}
									for a full list of products your organization has requested or downloaded.
									You may need to log in again to access this page.
								</Box>
							</>
						}
						isOpen={showSuccessModal}
						onClose={() => {
							reload();
							setShowSuccessModal(false);
						}}
						status="success"
						closeButtonText="Okay"
					/>
				</>
			)}
		</>
	);
});
